import * as React from 'react';
import { AxiosResponse } from 'axios';

import api from 'api';
import httpClient from 'api/httpClient';
import { useMutation } from 'hooks/query';
import {
  CalculateTempControlProjectBody,
  CreateTempControlProjectBody,
  DownloadTempControlProjectBody,
  TempControlPdfStatus,
  TempControlProject,
  TempControlProjectAuthorizationKey,
  UpdateTempControlProjectBody,
} from '__generated-api__';

import { readAuthKey, writeAuthKey } from 'tempcontrol/api/auth-keys';

export interface CreateProjectResult {
  project: TempControlProjectAuthorizationKey;
  /**
   * `null` for projects created by an authenticated user (no
   * anonymous-key path is available for owned projects); a
   * non-empty string for genuinely anonymous projects.
   */
  authorizationKey: string | null;
}

export interface DownloadProjectResult {
  blob: Blob;
  filename: string;
}

/**
 * Polling cadence for the PDF lifecycle. SPB renders are seconds-long
 * single-page documents, so we sample fast (1s) for the first few
 * polls and back off after that. Total ceiling matches the queue
 * job's 5-minute timeout - past that there's no point waiting.
 */
const PDF_POLL_INITIAL_INTERVAL_MS = 1000;
const PDF_POLL_BACKOFF_AFTER_ATTEMPTS = 5;
const PDF_POLL_BACKOFF_INTERVAL_MS = 3000;
const PDF_POLL_MAX_DURATION_MS = 300_000;

export class PdfRenderTimeoutError extends Error {
  public constructor() {
    super('PDF generation took longer than expected. Please try again.');
    this.name = 'PdfRenderTimeoutError';
  }
}

export class PdfRenderFailedError extends Error {
  public constructor(message?: string) {
    super(message || 'PDF generation failed. Please try again.');
    this.name = 'PdfRenderFailedError';
  }
}

/**
 * Imperative wrapper around the four `api.tempcontrol.*` operations.
 *
 * Wizard transitions are driven by explicit user actions ("Save & continue"),
 * so every endpoint is exposed as a mutation rather than a query - including
 * load. The hook also lazily reaches into the auth-key localStorage helper
 * so callers don't have to thread the auth key through every call.
 */
export const useTempControlProject = () => {
  const [createMutation, createState] = useMutation(api.tempcontrol.createTempControlProject);
  const [updateMutation, updateState] = useMutation(api.tempcontrol.updateTempControlProject);
  const [calculateMutation, calculateState] = useMutation(api.tempcontrol.calculateTempControlProject);
  const [loadMutation, loadState] = useMutation(api.tempcontrol.getTempControlProject);
  const [deleteMutation, deleteState] = useMutation(api.tempcontrol.deleteTempControlProject);
  const [downloadMutation, downloadState] = useMutation(api.tempcontrol.downloadTempControlProject);

  const createProject = React.useCallback(
    async (body: CreateTempControlProjectBody): Promise<CreateProjectResult> => {
      const response = await createMutation([{ createTempControlProjectBody: body }]);
      const data = response.data;
      // Owner-bound projects come back with a null `authorization_key`
      // (the backend deliberately doesn't issue one - they're gated
      // by the bearer-token policy). Skip the localStorage write so
      // we don't persist a useless / null value the wizard would
      // later try to send as an `Authorization-Key` header.
      const authorizationKey = data.authorization_key ?? null;
      if (authorizationKey) {
        writeAuthKey(data.key, authorizationKey);
      }
      return { project: data, authorizationKey };
    },
    [createMutation]
  );

  const loadProject = React.useCallback(
    async (key: string): Promise<TempControlProject> => {
      const response = await loadMutation([{ key, authorizationKey: readAuthKey(key) }]);
      return response.data;
    },
    [loadMutation]
  );

  const updateProject = React.useCallback(
    async (key: string, body: UpdateTempControlProjectBody): Promise<TempControlProject> => {
      const response = await updateMutation([
        { key, updateTempControlProjectBody: body, authorizationKey: readAuthKey(key) },
      ]);
      return response.data;
    },
    [updateMutation]
  );

  const calculateProject = React.useCallback(
    async (key: string, body?: CalculateTempControlProjectBody): Promise<TempControlProject> => {
      const response = await calculateMutation([
        { key, calculateTempControlProjectBody: body, authorizationKey: readAuthKey(key) },
      ]);
      return response.data;
    },
    [calculateMutation]
  );

  const deleteProject = React.useCallback(
    async (key: string): Promise<void> => {
      await deleteMutation([{ key }]);
    },
    [deleteMutation]
  );

  /**
   * Poll the show endpoint until the PDF reaches a terminal state
   * (`ready` or `failed`) or the ceiling elapses. The signal lets
   * callers abort polling if the user navigates away mid-render.
   */
  const waitForPdfReady = React.useCallback(
    async (key: string, signal?: AbortSignal): Promise<TempControlProject> => {
      const started = Date.now();
      let attempt = 0;

      while (true) {
        if (signal?.aborted) {
          throw new DOMException('Aborted', 'AbortError');
        }

        const project = await loadProject(key);
        if (project.pdf_status === TempControlPdfStatus.Ready) {
          return project;
        }
        if (project.pdf_status === TempControlPdfStatus.Failed) {
          throw new PdfRenderFailedError();
        }

        if (Date.now() - started > PDF_POLL_MAX_DURATION_MS) {
          throw new PdfRenderTimeoutError();
        }

        const interval =
          attempt < PDF_POLL_BACKOFF_AFTER_ATTEMPTS ? PDF_POLL_INITIAL_INTERVAL_MS : PDF_POLL_BACKOFF_INTERVAL_MS;
        attempt += 1;
        await new Promise((resolve) => setTimeout(resolve, interval));
      }
    },
    [loadProject]
  );

  /**
   * Run the full SPB-parity download flow:
   *
   *   1. POST .../download with customer details. The backend
   *      captures them, marks the project completed, and dispatches
   *      a render job to the `pdf` queue (or 200s immediately if a
   *      cached render is still valid).
   *   2. Poll .../{key} until `pdf_status === 'ready'` (or failed).
   *   3. GET .../download as a binary stream and return the blob +
   *      filename so the caller can trigger the browser download.
   *
   * This mirrors `Version::startPublishing()` + `downloadVersion()`
   * in SPB - render out-of-process, stream from S3.
   */
  const downloadProject = React.useCallback(
    async (key: string, body: DownloadTempControlProjectBody, signal?: AbortSignal): Promise<DownloadProjectResult> => {
      const triggerResponse = await downloadMutation([
        { key, downloadTempControlProjectBody: body, authorizationKey: readAuthKey(key) },
      ]);

      let project = triggerResponse.data;
      if (project.pdf_status !== TempControlPdfStatus.Ready) {
        project = await waitForPdfReady(key, signal);
      }

      const streamResponse = await streamPdf(key, signal);
      const filename =
        parseAttachmentFilename(streamResponse.headers?.['content-disposition']) ?? buildFallbackFilename(project);
      return { blob: streamResponse.data, filename };
    },
    [downloadMutation, waitForPdfReady]
  );

  /**
   * Stream-only variant for when a `ready` PDF is already cached on
   * the server (e.g. portal "Download PDF" button on a previously
   * downloaded project). Skips the trigger + poll round-trip.
   */
  const streamProjectPdf = React.useCallback(
    async (key: string, signal?: AbortSignal): Promise<DownloadProjectResult> => {
      const response = await streamPdf(key, signal);
      const filename = parseAttachmentFilename(response.headers?.['content-disposition']) ?? `tempcontrol-${key}.pdf`;
      return { blob: response.data, filename };
    },
    []
  );

  return {
    createProject,
    loadProject,
    updateProject,
    calculateProject,
    downloadProject,
    streamProjectPdf,
    waitForPdfReady,
    deleteProject,
    isCreating: createState.isLoading,
    isLoading: loadState.isLoading,
    isUpdating: updateState.isLoading,
    isCalculating: calculateState.isLoading,
    isDownloading: downloadState.isLoading,
    isDeleting: deleteState.isLoading,
  };
};

/**
 * Direct httpClient call to the binary stream endpoint.
 *
 * The OpenAPI spec describes `streamTempControlProjectPdf` as
 * returning `application/pdf` binary, but the generator wires
 * typescript-axios to JSON by default. Bypassing the generated
 * client and calling httpClient directly with `responseType: 'blob'`
 * is the cleanest fix; the URL contract is still anchored on the
 * spec so any rename will surface here as a 404. Using `httpClient`
 * (instead of bare axios) keeps the bearer token, refresh-token
 * interceptor, and baseURL configuration intact for portal users.
 */
const streamPdf = async (key: string, signal?: AbortSignal): Promise<AxiosResponse<Blob>> => {
  const authKey = readAuthKey(key);
  return httpClient.request<Blob>({
    method: 'GET',
    url: `/tempcontrol/projects/${encodeURIComponent(key)}/download`,
    headers: authKey ? { 'Authorization-Key': authKey } : undefined,
    responseType: 'blob',
    signal,
  });
};

/**
 * Last-resort filename when the server didn't send a
 * `Content-Disposition`. Mirrors the controller's slugify so the
 * filenames stay stable across the two paths.
 */
const buildFallbackFilename = (project: TempControlProject): string => {
  const slug = (project.project_name ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  return `tempcontrol-${slug || 'project'}.pdf`;
};

/**
 * Pull the filename out of a `Content-Disposition` header, if the
 * server sent one. Best-effort; falls back to a sensible default at
 * the call site when this returns undefined.
 */
const parseAttachmentFilename = (header: string | undefined): string | undefined => {
  if (!header) return undefined;
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
  if (!match) return undefined;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
};
